import React, { cloneElement, isValidElement, useRef } from "react";

import { useDropdownMenu } from "./context";

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  /** If true, merges props onto the child element instead of wrapping */
  asChild?: boolean;
}

/**
 * DropdownMenuTrigger - The button that toggles the dropdown
 * Supports asChild pattern for custom trigger elements
 */
export function DropdownMenuTrigger({
  children,
  asChild = false,
}: DropdownMenuTriggerProps) {
  const { open, setOpen, triggerRef } = useDropdownMenu();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Sync refs - use local ref for button, update context ref
  React.useEffect(() => {
    if (buttonRef.current && triggerRef) {
      (triggerRef as React.MutableRefObject<HTMLElement | null>).current =
        buttonRef.current;
    }
  }, [triggerRef]);

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(!open);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen(!open);
    }
    if (event.key === "ArrowDown" && !open) {
      event.preventDefault();
      setOpen(true);
    }
  };

  if (asChild && isValidElement(children)) {
    const childElement = children as React.ReactElement<{
      onClick?: (event: React.MouseEvent) => void;
      ref?: React.Ref<HTMLElement>;
      "aria-expanded"?: boolean;
      "aria-haspopup"?: string;
    }>;

    return cloneElement(childElement, {
      ref: (el: HTMLElement | null) => {
        if (triggerRef) {
          (triggerRef as React.MutableRefObject<HTMLElement | null>).current =
            el;
        }
      },
      "aria-haspopup": "menu",
      "aria-expanded": open,
      onClick: (event: React.MouseEvent) => {
        handleClick(event);
        // Also call the child's onClick if it exists
        childElement.props?.onClick?.(event);
      },
    });
  }

  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-haspopup="menu"
      aria-expanded={open}
    >
      {children}
    </button>
  );
}
